import { HTTP_STATUS, RESPONSE_CODES, RESPONSE_DETAIL_FIELDS } from '../consts';
import { router } from '../router';

/**
 * Structured, non-prose data accompanying an error response. The backend sends
 * raw values only (never server-composed sentences) so the SPA can interpolate
 * them into a localised message.
 *
 * Keys are derived from RESPONSE_DETAIL_FIELDS so each field is named in exactly
 * one place; add new fields there, not here.
 */
export type ApiErrorDetails = Partial<
  Record<(typeof RESPONSE_DETAIL_FIELDS)[keyof typeof RESPONSE_DETAIL_FIELDS], string[]>
>;

/**
 * ApiError — thrown for non-2xx responses that the caller must handle.
 * Note: 401 and 500 trigger a full-page browser redirect inside apiFetch
 * and are never received by callers. Callers will receive ApiError for
 * 400, 404, 409, and other non-5xx/non-401 status codes.
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly code?: string,
    public readonly details?: ApiErrorDetails,
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Base fetch wrapper for all API calls.
 * - Always includes credentials (saisoku-sid cookie)
 * - 401 → full-page redirect to /auth/login, preserving current URL
 * - 500 → SPA navigation to /error (preserves app state, no reload)
 * - 400, 404, and other non-OK (incl. 502/503) → throws ApiError for caller to handle
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...options,
    credentials: 'include', // always send saisoku-sid cookie
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...options?.headers,
    },
  });

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    // Session expired — redirect browser for full re-auth, preserving current URL
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(
      window.location.pathname + window.location.search,
    )}`;
    return Promise.reject(new ApiError(HTTP_STATUS.UNAUTHORIZED, RESPONSE_CODES.UNAUTHORIZED));
  }

  if (response.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
    // Unexpected server error — navigate to the error screen without full page reload
    void router.navigate('/error');
    return Promise.reject(new ApiError(HTTP_STATUS.INTERNAL_SERVER_ERROR, 'INTERNAL_SERVER_ERROR'));
  }

  if (!response.ok) {
    let code: string | undefined;
    let details: ApiErrorDetails | undefined;
    try {
      const body = (await response.json()) as Record<string, unknown>;
      if (typeof body.code === 'string') code = body.code;
      // Plain objects only — typeof null and typeof [] are both 'object',
      // and neither can satisfy ApiErrorDetails.
      if (
        typeof body.details === 'object' &&
        body.details !== null &&
        !Array.isArray(body.details)
      ) {
        details = body.details as ApiErrorDetails;
      }
    } catch {
      // ignore parse errors — code and details stay undefined
    }
    throw new ApiError(response.status, `API error: ${response.status}`, code, details);
  }

  // 204 No Content — no body to parse, return void cast as T
  if (response.status === HTTP_STATUS.NO_CONTENT) {
    return undefined as unknown as T;
  }

  // Wrap JSON parsing: unexpected HTML bodies would throw
  // a SyntaxError that is not an ApiError and would bypass callers' catch blocks.
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(response.status, `Failed to parse response body`);
  }
}
